/**
 * Admin management API client.
 */
import { apiDelete, apiGet, apiPost, apiPut } from './client';
import type {
  AddCourseDirectorRequestDto,
  CourseDirectorDto,
  CreateTrialEventNoteRequestDto,
  FormTemplateCreateRequestDto,
  FormTemplateDetailDto,
  FormTemplateListItemDto,
  FormTemplateUpdateRequestDto,
  OrganizationCreateRequestDto,
  OrganizationDetailDto,
  OrganizationListItemDto,
  OrganizationSportCodeCreateRequestDto,
  OrganizationSportCodeDetailDto,
  OrganizationSportCodeListItemDto,
  OrganizationSportCodeUpdateRequestDto,
  OrganizationUpdateRequestDto,
  PaginatedTrialEventSummaryResponse,
  TrialEventAdminDetailDto,
  TrialEventMutationResultDto,
  TrialEventNoteDto,
  TrialEventSummaryDto,
  TrialEventUpsertRequestDto,
} from './types';

export interface AdminTrialEventsParams {
  includeInactive?: boolean;
  dateFrom?: string;
  dateTo?: string;
  organizationCode?: string;
  sportCode?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

type UploadFile = Blob | File;

function setOptionalSearchParam(
  searchParams: URLSearchParams,
  key: string,
  value: string | number | boolean | null | undefined
): void {
  if (value === undefined || value === null || value === '') {
    return;
  }

  searchParams.set(key, String(value));
}

function buildQueryString(searchParams: URLSearchParams): string {
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

function buildJsonMultipartFormData(
  jsonField: string,
  jsonValue: unknown,
  fileField: string,
  file?: UploadFile | null
): FormData {
  const formData = new FormData();
  formData.set(jsonField, JSON.stringify(jsonValue));

  if (file) {
    formData.set(fileField, file);
  }

  return formData;
}

const organizationSportCodesApi = {
  list: (organizationId: string) =>
    apiGet<OrganizationSportCodeListItemDto[]>(
      `/admin/organizations/${organizationId}/sport-codes`
    ),

  getById: (organizationId: string, organizationSportCodeId: string) =>
    apiGet<OrganizationSportCodeDetailDto>(
      `/admin/organizations/${organizationId}/sport-codes/${organizationSportCodeId}`
    ),

  create: (
    organizationId: string,
    request: OrganizationSportCodeCreateRequestDto
  ) => apiPost<OrganizationSportCodeDetailDto>(
    `/admin/organizations/${organizationId}/sport-codes`,
    request
  ),

  update: (
    organizationId: string,
    organizationSportCodeId: string,
    request: OrganizationSportCodeUpdateRequestDto
  ) => apiPut<OrganizationSportCodeDetailDto>(
    `/admin/organizations/${organizationId}/sport-codes/${organizationSportCodeId}`,
    request
  ),

  delete: (organizationId: string, organizationSportCodeId: string) =>
    apiDelete<void>(
      `/admin/organizations/${organizationId}/sport-codes/${organizationSportCodeId}`
    ),
};

export const adminApi = {
  trialEvents: {
    list: (params: AdminTrialEventsParams = {}) => {
      const searchParams = new URLSearchParams();
      searchParams.set('includeInactive', String(params.includeInactive ?? false));
      setOptionalSearchParam(searchParams, 'dateFrom', params.dateFrom);
      setOptionalSearchParam(searchParams, 'dateTo', params.dateTo);
      setOptionalSearchParam(searchParams, 'organizationCode', params.organizationCode);
      setOptionalSearchParam(searchParams, 'sportCode', params.sportCode);
      setOptionalSearchParam(searchParams, 'search', params.search);
      setOptionalSearchParam(searchParams, 'page', params.page);
      setOptionalSearchParam(searchParams, 'pageSize', params.pageSize);

      return apiGet<PaginatedTrialEventSummaryResponse>(
        `/admin/trial-events${buildQueryString(searchParams)}`
      );
    },

    getById: (trialEventId: string) =>
      apiGet<TrialEventAdminDetailDto>(`/admin/trial-events/${trialEventId}`),

    create: (request: TrialEventUpsertRequestDto, premiumPdf: UploadFile) =>
      apiPost<TrialEventMutationResultDto>(
        '/admin/trial-events',
        buildJsonMultipartFormData('trialEvent', request, 'premiumPdf', premiumPdf)
      ),

    update: (
      trialEventId: string,
      request: TrialEventUpsertRequestDto,
      etag: string,
      premiumPdf?: UploadFile | null
    ) =>
      apiPut<TrialEventMutationResultDto>(
        `/admin/trial-events/${trialEventId}`,
        buildJsonMultipartFormData('trialEvent', request, 'premiumPdf', premiumPdf),
        { headers: { 'If-Match': etag } }
      ),

    deactivate: (trialEventId: string) =>
      apiPost<TrialEventSummaryDto>(`/admin/trial-events/${trialEventId}/deactivate`),

    reactivate: (trialEventId: string) =>
      apiPost<TrialEventSummaryDto>(`/admin/trial-events/${trialEventId}/reactivate`),

    listNotes: (trialEventId: string) =>
      apiGet<TrialEventNoteDto[]>(`/admin/trial-events/${trialEventId}/notes`),

    createNote: (trialEventId: string, request: CreateTrialEventNoteRequestDto) =>
      apiPost<TrialEventNoteDto>(`/admin/trial-events/${trialEventId}/notes`, request),

    addCourseDirector: (trialEventId: string, request: AddCourseDirectorRequestDto) =>
      apiPost<CourseDirectorDto>(`/admin/trial-events/${trialEventId}/course-directors`, request),

    removeCourseDirector: (trialEventId: string, email: string) =>
      apiDelete<void>(
        `/admin/trial-events/${trialEventId}/course-directors/${encodeURIComponent(email)}`
      ),
  },

  organizations: {
    list: () => apiGet<OrganizationListItemDto[]>('/admin/organizations'),

    getById: (organizationId: string) =>
      apiGet<OrganizationDetailDto>(`/admin/organizations/${organizationId}`),

    create: (request: OrganizationCreateRequestDto) =>
      apiPost<OrganizationDetailDto>('/admin/organizations', request),

    update: (organizationId: string, request: OrganizationUpdateRequestDto) =>
      apiPut<OrganizationDetailDto>(`/admin/organizations/${organizationId}`, request),

    delete: (organizationId: string) => apiDelete<void>(`/admin/organizations/${organizationId}`),

    sportCodes: organizationSportCodesApi,
  },

  formTemplates: {
    list: () => apiGet<FormTemplateListItemDto[]>('/admin/form-templates'),

    getById: (formTemplateId: number | string) =>
      apiGet<FormTemplateDetailDto>(`/admin/form-templates/${formTemplateId}`),

    create: (request: FormTemplateCreateRequestDto, formPdf?: UploadFile | null) =>
      apiPost<FormTemplateDetailDto>(
        '/admin/form-templates',
        buildJsonMultipartFormData('formTemplate', request, 'formPdf', formPdf)
      ),

    update: (
      formTemplateId: number | string,
      request: FormTemplateUpdateRequestDto,
      formPdf?: UploadFile | null
    ) =>
      apiPut<FormTemplateDetailDto>(
        `/admin/form-templates/${formTemplateId}`,
        buildJsonMultipartFormData('formTemplate', request, 'formPdf', formPdf)
      ),

    delete: (formTemplateId: number | string) =>
      apiDelete<void>(`/admin/form-templates/${formTemplateId}`),
  },
};